/**
 * Normalise a `/task/events/recent` frame into the plain tasks array the
 * Sidebar renders.
 *
 * Actual frame shape:
 *
 *   {
 *     "total": 20,
 *     "tasks": [
 *       { "parent_task": { "id", "title", "status", "created_at",
 *                          "type": "Original_run",
 *                          "subtasks": [ { "id", "title", "status",
 *                                          "created_at", "type": "Draft1" },
 *                                        … ] } },
 *       …
 *     ]
 *   }
 *
 * Each list item wraps the task under `parent_task`; we unwrap it so the
 * Sidebar reads the task fields (`id` / `title` / `status` / `created_at`)
 * and its nested `subtasks` directly. The stream pushes a FULL snapshot
 * per frame, so the consumer replaces its list wholesale. Tolerant of a
 * `data` envelope, the alternate list keys, and a flat (already-unwrapped)
 * shape so a backend tweak degrades to "no rows" rather than throwing.
 */
// Keys a frame may carry the list under.
const LIST_KEYS = ["tasks", "recent", "events", "items", "results"];

function frameBody(raw) {
  if (!raw || typeof raw !== "object") return null;
  // Unwrap a `data` envelope (which may itself be the array).
  return raw.data && typeof raw.data === "object" ? raw.data : raw;
}

/**
 * Whether a frame actually carries a recent-tasks list.
 *
 * A persistent stream doesn't only push snapshots — it can also send
 * heartbeats, acks, or other event types. Those normalise to an empty
 * array, and blindly applying that would BLANK the sidebar. Callers use
 * this to ignore anything that isn't a snapshot, while still honouring a
 * genuinely empty list (`{"tasks": []}` → the user has no tasks).
 */
export function isRecentTasksFrame(raw) {
  const body = frameBody(raw);
  if (!body) return false;
  if (Array.isArray(body)) return true;
  return LIST_KEYS.some((key) => Array.isArray(body[key]));
}

export function normalizeRecentTasks(raw) {
  const body = frameBody(raw);
  if (!body) return [];

  let list = [];
  if (Array.isArray(body)) {
    list = body;
  } else {
    for (const key of LIST_KEYS) {
      if (Array.isArray(body[key])) {
        list = body[key];
        break;
      }
    }
  }

  return list
    .map((item) => {
      // Preferred shape: { parent_task: { … } }. Fall back to the item
      // itself when it's already the task (flat / legacy shape).
      if (item && typeof item === "object" && item.parent_task) {
        const task = item.parent_task;
        // The source label ("new task" / "template run" / "trigger")
        // may live on the WRAPPER (sibling of parent_task). Carry it
        // down onto ``source`` so the Sidebar's ``sourceOf`` reads it
        // after the unwrap — onto ``source`` (not ``type``) so it never
        // collides with ``parent_task.type``, which is the RUN type
        // ("Original_run"). Never clobber a source the task already has.
        const wrapperSource = item.source ?? item.type;
        if (wrapperSource != null && task.source == null) {
          return { ...task, source: wrapperSource };
        }
        return task;
      }
      return item;
    })
    .filter((task) => task && typeof task === "object");
}
